import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
    Alert,
    Dialog,
    DialogContent,
    FormControlLabel,
    Switch,
    TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import AddEditModalHeader from '@/components/AddEditModalHeader';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useEntityImage } from '@/hooks/useEntityImage';
import { useIsGstTaxRegion } from '@/hooks/useIsGstTaxRegion';
import { COLOR } from '@/theme/colors';
import { InvoicingProductValidationSchema } from '@/validations/invoicingProduct';

type Product = {
    id: string;
    name: string;
    price: string;
    available: boolean;
};

type FormValues = {
    name: string;
    price: string;
    available: boolean;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (values: FormValues) => Promise<void> | void;
    product?: Product | null;
};

const initialValues: FormValues = {
    name: '',
    price: '',
    available: true,
};

function AddEditInvoicingProductModal({
    isOpen,
    onClose,
    onSave,
    product,
}: Props) {
    const { t } = useTranslation();
    const { userOrganisation, userOrganisations } = useOrganisationsContext();
    const teamAvatar = useEntityImage(userOrganisation?.id || '');
    const { isGstTaxRegion } = useIsGstTaxRegion();

    const isEditMode = !!product;

    const form = useFormik<FormValues>({
        initialValues: product
            ? {
                  name: product.name,
                  price: product.price,
                  available: product.available,
              }
            : initialValues,
        enableReinitialize: true,
        onSubmit: async (values) => {
            await onSave(values);
        },
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: false,
        validationSchema:
            InvoicingProductValidationSchema.createInvoicingProductForm,
    });

    const { resetForm } = form;

    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen, resetForm]);

    const showTeamName =
        userOrganisations && userOrganisations.length > 1
            ? userOrganisation?.name || ''
            : undefined;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, overflow: 'hidden' },
            }}
            data-test-id="AddEditInvoicingProductModal"
        >
            <AddEditModalHeader
                backgroundColor={COLOR.deepCerulean}
                cancel={onClose}
                save={form.handleSubmit}
                testIdPrefix="AddEditInvoicingProductModal"
                title={
                    isEditMode
                        ? t('InvoiceSystem:add_edit_products:edit_product')
                        : t('InvoiceSystem:add_edit_products:add_product')
                }
                teamName={showTeamName}
                avatarUrl={
                    showTeamName ? teamAvatar?.imageURL || '' : undefined
                }
                icon={
                    isEditMode ? (
                        <EditIcon sx={{ color: COLOR.white }} />
                    ) : (
                        <AddCircleOutlineIcon sx={{ color: COLOR.white }} />
                    )
                }
            />
            <DialogContent sx={{ pt: 3, maxWidth: 500 }}>
                <TextField
                    autoFocus
                    fullWidth
                    label={t('Procedure:label:name')}
                    name="name"
                    value={form.values.name}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    error={form.touched.name && !!form.errors.name}
                    helperText={form.touched.name && form.errors.name}
                    sx={{ mb: 2 }}
                    data-test-id="AddEditInvoicingProductModal-NameInput"
                />

                {isGstTaxRegion && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t(
                            'InvoiceSystem:add_edit_products:procedure_disclaimer',
                        )}{' '}
                        {t('InvoiceSystem:add_edit_products:procedure_define')}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label={t('InvoiceSystem:add_edit_products:price')}
                    name="price"
                    value={form.values.price}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    error={form.touched.price && !!form.errors.price}
                    helperText={form.touched.price && form.errors.price}
                    type="text"
                    inputMode="decimal"
                    sx={{ mb: 2 }}
                    data-test-id="AddEditInvoicingProductModal-PriceInput"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={form.values.available}
                            onChange={(e) =>
                                form.setFieldValue(
                                    'available',
                                    e.target.checked,
                                )
                            }
                            data-test-id="AddEditInvoicingProductModal-AvailableSwitch"
                        />
                    }
                    label={t('InvoiceSystem:add_edit_products:available')}
                />
            </DialogContent>
        </Dialog>
    );
}

export default AddEditInvoicingProductModal;
export type { FormValues, Product };
